import React, { useEffect, useMemo } from 'react';
import TaskFormSheet from './components/TaskFormSheet';
import { TaskKpiRow } from './components/TaskKpiRow';
import { TaskFilterChips } from './components/TaskFilterChips';
import { TaskGroupedList } from './components/TaskGroupedList';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import BulkActionsBar, { BulkAction } from '../../components/shared/BulkActionsBar';
import AgentsFilter from '../../components/shared/filters/AgentsFilter';
import DateRangePicker from '../../components/shared/filters/DateRangePicker';
import SearchInput from '../../components/ui/SearchInput';

import { TrashIcon } from '../../components/icons';
import { Button } from '../../components/ui/Button';
import { useTasksPage } from './hooks/useTasksPage';
import { useUsers } from '../../contexts/users/UsersContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/shared/Pagination';
import { PageHeader } from '../../components/layout/Header';
import { MOBILE_CONTENT_BOTTOM_PAD_CLASS } from '../../components/layout/layoutNavTokens';
import { cn } from '@/lib/utils';

const TasksFeature: React.FC = () => {
    const { user } = useAuth();
    const { isMember, canFilterByAgent } = usePermissions();
    const { crmUsers } = useUsers();

    const {
        groups, kpis, chipCounts, pendingCount, chip, setChip,
        dateRange, setDateRange,
        totalCount, currentPage, setCurrentPage, pageSize,
        loading, searchTerm, setSearchTerm,
        assignedToFilter, setAssignedToFilter,
        selectedIds, toggleSelection, clearSelection,
        isFormModalOpen, setIsFormModalOpen, taskToEdit,
        isDeleteModalOpen, setIsDeleteModalOpen, taskToDelete,
        isBulkDeleteModalOpen, setIsBulkDeleteModalOpen,
        handleOpenCreateModal, handleOpenEditModal, handleOpenDeleteModal,
        handleSaveTask, handleConfirmDelete, handleDuplicate, handleToggleCompletion,
        handleBulkDelete
    } = useTasksPage(user?.id);

    // Se for member, força filtro de assignedTo para ele mesmo
    useEffect(() => {
        if (isMember && user?.id) {
            setAssignedToFilter([user.id]);
        }
    }, [isMember, user?.id, setAssignedToFilter]);

    const bulkActions: BulkAction[] = useMemo(() => [
        { label: 'Excluir', icon: TrashIcon, onClick: () => setIsBulkDeleteModalOpen(true), variant: 'danger' as const }
    ], [setIsBulkDeleteModalOpen]);

    return (
        <>
            <div className="flex h-full min-w-0 flex-col overflow-x-hidden">
                <header className="shrink-0 border-b border-border bg-background px-s4 pb-s3 pt-s3 md:border-0 md:bg-transparent md:px-s6 md:pb-0 md:pt-s4">
                    <PageHeader
                        title={
                            <>
                                Tarefas{' '}
                                <span className="text-sm font-normal text-muted">
                                    {pendingCount} pendentes
                                </span>
                            </>
                        }
                        subtitle="Gerencie suas tarefas e pendências de vendas"
                        titleClassName="text-lg md:text-xl"
                        subtitleClassName="text-sm text-muted"
                        className="mb-0 gap-s3 md:mb-s5 md:gap-s3"
                        actionClassName="w-full md:hidden"
                        hideBackButton
                        action={
                            <div className="flex w-full flex-col gap-s3">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Pesquisar tarefas..."
                                    className="min-h-11 w-full"
                                />
                                <Button
                                    type="button"
                                    onClick={handleOpenCreateModal}
                                    className="min-h-11 w-full touch-manipulation"
                                >
                                    Nova Tarefa
                                </Button>
                            </div>
                        }
                    />
                </header>

                <div className={cn(
                    'flex min-h-0 min-w-0 flex-1 flex-col gap-s5 overflow-x-hidden overflow-y-auto overscroll-y-contain px-s4 pt-s3 md:gap-s4 md:px-s6 md:pb-s6 md:pt-0',
                    MOBILE_CONTENT_BOTTOM_PAD_CLASS,
                    selectedIds.size > 0 && 'max-md:pb-[max(9.5rem,calc(env(safe-area-inset-bottom)+8.5rem))]',
                )}>
                    <TaskKpiRow kpis={kpis} onSelectChip={setChip} activeChip={chip} />

                    <section
                        aria-label="Filtros"
                        className="min-w-0 w-full"
                    >
                    <div
                        className={cn(
                            'flex min-h-11 w-full min-w-0 flex-nowrap items-center gap-s2',
                            'overflow-x-auto overflow-y-hidden overscroll-x-contain',
                            '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
                            'md:flex-wrap md:overflow-visible',
                        )}
                    >
                        <TaskFilterChips active={chip} onChange={setChip} counts={chipCounts} />
                        <DateRangePicker value={dateRange} onChange={setDateRange} surface="pill" />
                        {canFilterByAgent && (
                            <div className="shrink-0">
                                <AgentsFilter
                                    users={crmUsers}
                                    selectedAgentIds={assignedToFilter}
                                    onApply={setAssignedToFilter}
                                    currentUserId={user?.id}
                                    surface="pill"
                                />
                            </div>
                        )}
                        <div className="hidden md:ml-auto md:flex md:items-center md:gap-s2">
                            <Button
                                type="button"
                                onClick={handleOpenCreateModal}
                                className="shrink-0 whitespace-nowrap"
                            >
                                Nova Tarefa
                            </Button>
                            <div className="w-64">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Pesquisar tarefas..."
                                />
                            </div>
                        </div>
                    </div>
                    </section>

                    <section aria-label="Lista de tarefas" className="flex flex-col gap-s4">
                        <TaskGroupedList
                            groups={groups}
                            isLoading={loading}
                            selectedIds={selectedIds}
                            onToggleSelect={toggleSelection}
                            onToggleCompletion={handleToggleCompletion}
                            onEdit={handleOpenEditModal}
                            onDelete={handleOpenDeleteModal}
                            onDuplicate={handleDuplicate}
                        />

                        <Pagination
                            className="mt-0 shrink-0"
                            currentPage={currentPage}
                            totalCount={totalCount}
                            pageSize={pageSize}
                            onPageChange={setCurrentPage}
                            selectedCount={selectedIds.size}
                        />
                    </section>
                </div>
            </div>

            <BulkActionsBar selectedCount={selectedIds.size} actions={bulkActions} onClose={clearSelection} />

            <TaskFormSheet isOpen={isFormModalOpen} onClose={() => setIsFormModalOpen(false)} onSave={handleSaveTask} task={taskToEdit} />
            <ConfirmationModal
                isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={handleConfirmDelete}
                title="Excluir Tarefa" message={`Tem certeza que deseja excluir a tarefa "${taskToDelete?.title}"?`}
                confirmButtonText="Excluir" confirmButtonColor="red"
            />
            <ConfirmationModal
                isOpen={isBulkDeleteModalOpen} onClose={() => setIsBulkDeleteModalOpen(false)} onConfirm={handleBulkDelete}
                title="Excluir Tarefas" message={`Tem certeza que deseja excluir ${selectedIds.size} tarefa(s)?`}
                confirmButtonText="Excluir" confirmButtonColor="red"
            />
        </>
    );
};

export default TasksFeature;
